import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import UpgradeModal from './components/UpgradeModal';
import SessionWarning from './components/SessionWarning';
import LoginPage from './pages/LoginPage';
import ActivatePage from './pages/ActivatePage';
import DashboardPage from './pages/DashboardPage';
import AccountPage from './pages/AccountPage';
import PersonasPage from './pages/PersonasPage';
import CreatePersonaPage from './pages/CreatePersonaPage';
import FriendRequestsPage from './pages/FriendRequestsPage';
import FriendPersonasPage from './pages/FriendPersonasPage';
import PersonaPrivacyPage from './pages/PersonaPrivacyPage';
import ProductsPage from './pages/ProductsPage';
import ProfilePage from './pages/ProfilePage';
import GroupsPage from './pages/GroupsPage';
import WishListsPage from './pages/WishListsPage';
import VisualizationsPage from './pages/VisualizationsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSubscriptionsPage from './pages/AdminSubscriptionsPage';
import AdminEnvironmentPage from './pages/AdminEnvironmentPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import AdminSystemPage from './pages/AdminSystemPage';
import AdminWaitlistPage from './pages/AdminWaitlistPage';
import AdminProductImportPage from './pages/AdminProductImportPage';
import LandingPage from './pages/LandingPage';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import FloatingChatBubble from './components/FloatingChatBubble';
import AdminSearchAnalyticsPage from './pages/AdminSearchAnalyticsPage';
import AIConciergePage from './pages/AIConciergePage';

// Protected Route component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Main App content
function AppContent() {
  const { isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  // Matches Sidebar.js's own `hiddenFrom/visibleFrom="md"` switch (Mantine's
  // md breakpoint, 62em) between the mobile Drawer and the fixed desktop
  // rail — only reserve the 280px content margin once the fixed rail is
  // actually on screen.
  const isDesktopSidebar = useMediaQuery('(min-width: 62em)');

  const handleMenuClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <Router>
      <SessionWarning />
      <FloatingChatBubble />
      <div style={{ minHeight: '100vh' }}>
        {isAuthenticated && <Navbar onMenuClick={handleMenuClick} />}
        {isAuthenticated && <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />}
        <div style={{
          paddingTop: isAuthenticated ? '64px' : '0',
          marginLeft: isAuthenticated && isDesktopSidebar ? '280px' : '0',
          transition: 'margin-left 0.3s ease'
        }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />} />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route path="/activate" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ActivatePage />} />
            {/* /register used to be an open self-serve signup page — replaced by
                the beta-request form (BetaSignupModal) + /activate. Redirect any
                stale bookmarked/emailed links rather than 404ing them. */}
            <Route path="/register" element={<Navigate to="/login" />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/personas" element={
              <ProtectedRoute>
                <PersonasPage />
              </ProtectedRoute>
            } />
            <Route path="/personas/new" element={
              <ProtectedRoute>
                <CreatePersonaPage />
              </ProtectedRoute>
            } />
            <Route path="/friends" element={
              <ProtectedRoute>
                <FriendRequestsPage />
              </ProtectedRoute>
            } />
            <Route path="/friends/:friendId/personas" element={
              <ProtectedRoute>
                <FriendPersonasPage />
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute>
                <ProductsPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/groups" element={
              <ProtectedRoute>
                <GroupsPage />
              </ProtectedRoute>
            } />
            <Route path="/groups/new" element={
              <ProtectedRoute>
                <GroupsPage />
              </ProtectedRoute>
            } />
            <Route path="/wishlists" element={
              <ProtectedRoute>
                <WishListsPage />
              </ProtectedRoute>
            } />
            <Route path="/ai-concierge" element={
              <ProtectedRoute>
                <AIConciergePage />
              </ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } />
            <Route path="/persona-privacy" element={
              <ProtectedRoute>
                <PersonaPrivacyPage />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <VisualizationsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/users" element={
              <ProtectedRoute adminOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/subscriptions" element={
              <ProtectedRoute adminOnly>
                <AdminSubscriptionsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/environment" element={
              <ProtectedRoute adminOnly>
                <AdminEnvironmentPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/search-analytics" element={
              <ProtectedRoute adminOnly>
                <AdminSearchAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute adminOnly>
                <AdminAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/system" element={
              <ProtectedRoute adminOnly>
                <AdminSystemPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/products/import" element={
              <ProtectedRoute adminOnly>
                <AdminProductImportPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/waitlist" element={
              <ProtectedRoute adminOnly>
                <AdminWaitlistPage />
              </ProtectedRoute>
            } />
            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

// Mantine theme configuration
const theme = createTheme({
  primaryColor: 'blue',
  defaultRadius: 'md',
});

function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <AuthProvider>
        <SubscriptionProvider>
          <AppContent />
          <UpgradeModal />
        </SubscriptionProvider>
      </AuthProvider>
    </MantineProvider>
  );
}

export default App;
